<template>
  <div class="page-container">
    <h5 class="title-container">
      <b class="title" id="page-title">{{ $t('SAAI.SetFeedback.title') }}</b>
    </h5>
    <p class="text-danger">
      {{ $t('SAAI.SetFeedback.explanation1') }} <br />
      {{ $t('SAAI.SetFeedback.explanation2') }}
    </p>
    <p class="choose-interval">
      <label for="chooseInterval" class="part-name">{{ $t('SAAI.SetFeedback.level') }}</label>
      <select id="chooseInterval" @change="changeInterval">
        <option
          v-for="item in 6"
          :key="item"
          :value="item"
          :selected="feedbackInterval == item ? true : false"
        >
          {{ item }}
        </option>
      </select>
    </p>
    <div class="interval-container flex-row flex-spbt">
      <div class="flex-row flex-center chart-wrapper interval-graph-container">
        <AnalysisChart :chart-data="chartData" :line-index="lineIndex" />
      </div>
      <div class="interval-table-container">
        <div class="view-feedback-table">
          <TabulatorComponent
            v-model="feedbackData.data"
            :options="feedbackData.options"
            id="feedback-table"
          />
        </div>
      </div>
    </div>
    <div class="button-wrapper" id="range-implementation-button-wrapper">
      <button @click="setTableIntervalFromFeedbackTable" id="range-implementation-button">
        {{ $t('SAAI.SetFeedback.apply') }}
      </button>
    </div>
    <div class="result-container">
      <h5 class="title-container title-analysis-result-container">
        <b class="title">{{ $t('SAAI.SetFeedback.result') }}</b>
      </h5>
      <div class="view-result">
        <TabulatorComponent
          v-model="resultData.scoringResults"
          :options="resultData.options"
          class="table"
          id="result-table"
        />
      </div>
    </div>
    <div class="button-wrapper grading-complete-button-wrapper">
      <button
        @click="
          saveFinalAnalysisResult();
          changeSheet();
        "
      >
        {{ $t('SAAI.SetFeedback.finish') }}
      </button>
    </div>
  </div>
</template>

<script>
import { TabulatorComponent } from 'vue-tabulator';
import AnalysisChart from '@/components/Saai/SetFeedbackPage/AnalysisChart.vue';
import { mapActions, mapState } from 'vuex';
import { range, toInteger } from 'lodash';

export default {
  name: 'AnalysisResult',
  components: {
    TabulatorComponent,
    AnalysisChart,
  },
  data() {
    let lineIndex = [];
    let colorList = ['Pink', 'lightyellow', 'lightblue', 'lightsteelblue', 'Thistle', 'white'];
    return {
      lineIndex,
      colorList,
      feedbackInterval: 3,
      setColor(newIndex) {
        lineIndex = newIndex;
      },
      basicData: [],
      resultData: {
        scoringResults: [],
        options: {
          columns: [
            { title: 'id', field: 'id', width: `7%` },
            {
              title: this.$i18n.locale === 'eng' ? 'response' : '응답',
              field: 'answer',
              width: `27%`,
            },
            {
              title: this.$i18n.locale === 'eng' ? 'interval' : '구간',
              field: 'interval',
              width: `8%`,
              editor: 'select',
              editorParams: { values: { 1: '1', 2: '2', 3: '3' } },
              cellEdited: function (cell) {
                cell.getRow().getElement().style.backgroundColor = colorList[cell.getValue() - 1];
                cell.setValue(parseInt(cell.getValue()));
              },
            },
            {
              title: this.$i18n.locale === 'eng' ? 'num' : '단어수',
              field: 'number_of_import_words',
              width: `8%`,
            },
            {
              title: this.$i18n.locale === 'eng' ? 'score' : '점수',
              field: 'score',
              formatter: function (cell) {
                return cell.getValue().toFixed(4);
              },
              width: `8%`,
            },
            {
              title: this.$i18n.locale === 'eng' ? 'missing important keywords' : '빠진 중요어',
              field: 'missing_important_word',
              width: `30%`,
              headerSort: false,
            },
            {
              title: this.$i18n.locale === 'eng' ? 'feedback' : '피드백 문구',
              field: 'feedbackComment',
              width: `12%`,
            },
          ],
          rowFormatter(row) {
            row.getElement().style.backgroundColor = colorList[row.getData().interval - 1];
          },
        },
      },
      chartData: {
        labels: [],
        datasets: [
          {
            label: 'Data One',
            backgroundColor: '#F87979',
            data: [],
          },
        ],
      },
      feedbackData: {
        data: [],
        options: {
          height: '18vh',
          minHeight: '200px',
          maxHeight: '250px',
          columns: [
            {
              title: this.$i18n.locale === 'eng' ? 'interval' : '구간',
              field: 'interval',
              width: `20%`,
              headerSort: false,
            },
            {
              title: this.$i18n.locale === 'eng' ? 'start' : '시작',
              field: 'startScope',
              width: `15%`,
              editor: 'input',
              headerSort: false,
            },
            {
              title: this.$i18n.locale === 'eng' ? 'end' : '종료',
              field: 'endScope',
              width: `15%`,
              editor: 'input',
              headerSort: false,
            },
            {
              title: this.$i18n.locale === 'eng' ? 'feedback' : '피드백 문구',
              field: 'feedback',
              width: `50%`,
              editor: 'input',
              headerSort: false,
            },
          ],
        },
      },
      feedbackList: [
        '훌륭한 설명입니다.',
        '좋은 설명입니다.',
        '잘했습니다.',
        '내용 보충이 필요합니다.',
        '내용 숙지가 부족합니다.',
        '보충 학습이 필요합니다.',
      ],
    };
  },
  computed: {
    ...mapState('saaiPaper', {
      analysisResults: (state) => state.analysisResults,
      nowSheetNumber: (state) => state.nowSheetNumber,
      numberOfSheets: (state) => state.numberOfSheets,
    }),
    getDatasets() {
      const data = this.chartData.datasets[0].data;
      for (const i of this.resultData.scoringResults) {
        data.push(i.score);
      }
    },
    getLabels() {
      const labels = this.chartData.labels;
      labels.length = this.resultData.scoringResults.length;
      labels.fill('');
      labels[0] = this.$i18n.locale === 'eng' ? `Max` : '최대점';
      labels[labels.length - 1] = this.$i18n.locale === 'eng' ? `Min` : '최소점';
    },
  },
  methods: {
    ...mapActions('saaiPaper', [
      'AC_SET_NEXT_SHEET',
      'AC_SET_FINAL_ANALYSIS_RESULTS',
      'AC_INIT_NOW_SHEET_NUMBER',
    ]),
    compareScore(a, b) {
      return b.score - a.score;
    },
    sortData(data) {
      let newData = data.sort(this.compareScore);
      for (const i in newData) {
        const object = newData[i];
      }
      return newData;
    },
    setData() {
      let sortedBasicData = this.sortData(this.basicData);
      this.resultData.scoringResults = this.setTableInterval(sortedBasicData, this.lineIndex);
      this.setFeedbackComment();
    },
    changeInterval(event) {
      if (
        confirm(
          this.$i18n.locale === 'eng'
            ? `Change the feedback interval level?`
            : '피드백 구간 수준을 변경하시겠습니까?'
        )
      ) {
        this.newInterval(event.target.value);
        this.feedbackInterval = event.target.value;
        this.newEditorParams();
      } else {
        event.target.value = this.feedbackData.data.length;
      }
    },
    newEditorParams() {
      let newNumberOfIntervals = this.feedbackInterval;
      let newEditorParamsValues = {};
      for (let i in range(newNumberOfIntervals)) {
        let key = toInteger(i) + 1;
        newEditorParamsValues[key] = key;
      }
      this.resultData.options.columns[2].editorParams.values = newEditorParamsValues;
    },
    newInterval(value) {
      const totalNumber = this.resultData.scoringResults.length;
      const share = parseInt(totalNumber / value);
      const rest = totalNumber % value;
      let newData = [];
      let lineIndex = [];
      newData.length = value;
      newData.fill('');

      let setting = { start: 1, share: share, rest: rest, len: value };
      for (const i in newData) {
        let object = {};
        object.interval = parseInt(i) + 1;
        this.findScope(i, lineIndex, setting);
        object.startScope = setting.start;
        object.endScope = setting.end;
        object.feedback = this.feedbackList[i];
        newData[i] = object;
        setting.start = setting.end + 1;
      }
      this.lineIndex = lineIndex;
      this.feedbackData.data = newData;
      this.setColor(lineIndex);
      this.setData();
    },
    findScope(i, lineIndex, setting) {
      let startScope = setting.start;
      let endScope;
      if (setting.rest != 0 && i > setting.len - setting.rest - 1) {
        endScope = startScope + setting.share;
      } else {
        endScope = startScope + setting.share - 1;
      }
      setting.end = endScope;
      lineIndex[i] = endScope;
      return i, setting, lineIndex;
    },
    setTableInterval(sortedBasicData, lineIndex) {
      for (let id = 1; id <= sortedBasicData.length; id++) {
        for (let idx = 0; idx < lineIndex.length; idx++) {
          if (id <= lineIndex[idx]) {
            sortedBasicData[id - 1]['interval'] = idx + 1;
            break;
          }
        }
      }
      return sortedBasicData;
    },
    setFeedbackComment() {
      const scoringResults = this.resultData.scoringResults;
      for (let i = 0; i < scoringResults.length; i++) {
        scoringResults[i].feedbackComment = this.feedbackList[scoringResults[i].interval - 1];
      }
    },
    editFeedbackComment() {
      const scoringResults = this.resultData.scoringResults;
      for (let i = 0; i < scoringResults.length; i++) {
        scoringResults[i].feedbackComment =
          this.feedbackData.data[scoringResults[i].interval - 1].feedback;
      }
    },
    setTableIntervalFromFeedbackTable() {
      let numberOfIntervals = this.feedbackData.data.length;
      let lineIndex = [];
      for (let itv = 0; itv < numberOfIntervals; itv++) {
        lineIndex.push(this.feedbackData.data[itv].endScope);
      }
      if (
        confirm(
          this.$i18n.locale === 'eng'
            ? `Change the feedback interval range?`
            : '피드백 구간 범위을 변경하시겠습니까?'
        )
      ) {
        this.lineIndex = lineIndex;
        let sortedBasicData = this.sortData(this.basicData);
        this.resultData.scoringResults = this.setTableInterval(sortedBasicData, this.lineIndex);
        this.editFeedbackComment();
      }
    },
    changeSheet() {
      if (this.nowSheetNumber === this.numberOfSheets - 1) {
        this.$router.push('/saai/viewResult');
      } else {
        this.AC_SET_NEXT_SHEET();
      }
    },
    saveFinalAnalysisResult() {
      const finalAnalysisResult = this.resultData.scoringResults;
      this.AC_SET_FINAL_ANALYSIS_RESULTS(finalAnalysisResult);
    },
  },
  watch: {
    '$i18n.locale': function (newLocale, oldLocale) {
      this.resultData.options.columns[1].title = newLocale === 'eng' ? 'response' : '응답';
      this.resultData.options.columns[2].title = newLocale === 'eng' ? 'interval' : '구간';
      this.resultData.options.columns[3].title = newLocale === 'eng' ? 'num' : '단어수';
      this.resultData.options.columns[4].title = newLocale === 'eng' ? 'score' : '점수';
      this.resultData.options.columns[5].title =
        newLocale === 'eng' ? 'missing important keywords' : '빠진 중요어';
      this.resultData.options.columns[6].title = newLocale === 'eng' ? 'feedback' : '피드백 문구';

      this.feedbackData.options.columns[0].title = newLocale === 'eng' ? 'interval' : '구간';
      this.feedbackData.options.columns[1].title = newLocale === 'eng' ? 'start' : '시작';
      this.feedbackData.options.columns[2].title = newLocale === 'eng' ? 'end' : '종료';
      this.feedbackData.options.columns[3].title = newLocale === 'eng' ? 'feedback' : '피드백 문구';
    },
  },
  created() {
    this.basicData = this.analysisResults[this.nowSheetNumber].undefined;
    this.setData();
    this.getDatasets;
    this.getLabels;
    this.newInterval(3);
  },
};
</script>

<style lang="scss" scoped>
.page-container {
  height: 100%;
  width: 100%;
}

.title-container {
  display: block;
  margin-bottom: 15px;
}

.title-analysis-result-container {
  margin-top: 0px;
}

.title {
  color: $MAIN-COLOR;
  border-bottom: 2px solid $MAIN-COLOR;
  padding: 5px 30px;
}

.choose-interval {
  margin-top: 10px;
}

.interval-container {
  margin-top: 10px;
}

.view-result {
  margin: 15px 0;
  height: 100%;
  padding: 0;
}

.view-feedback-table {
  height: 100%;
  padding: 0;
}

.table {
  background-color: white;
  max-height: 55vh !important;
}

#feedback-table {
  background-color: white;
  margin-bottom: 0px;
}

select {
  padding: 5px 10px;
}

.part-name {
  margin-right: 10px;
  span {
    font-weight: bold;
  }
}

.chart-wrapper {
  background-color: white;
  border: 1px solid $MAIN-COLOR;
  border-radius: 5px;
  height: 18vh;
  min-height: 200px;
  max-height: 250px;
}

#range-implementation-button-wrapper {
  margin-top: 20px;
  margin-bottom: 0px;
}

.interval-graph-container,
.interval-table-container {
  width: 46%;
}

.grading-complete-button-wrapper {
  justify-content: right;
  margin-top: 20px;
}
</style>
